/**
 * CustomerEditorPage
 *
 * Customer-facing editor page that reuses the shared V4 editor components
 * with customer-scoped API endpoints and restricted permissions.
 *
 * Key differences from dashboard EditorPage:
 * - Mode is 'customer' (hides admin-only elements and Publish button)
 * - Shows Approve & Request Changes buttons instead
 * - Uses customer-scoped API endpoints (no websiteId in URLs)
 * - No structural editing or AI commands
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { approveSite } from '../api/website';
import { applyStructuralChanges } from '../api/ai-editor';

// Shared editor components
import { EditorToolbar } from '../editor/components/EditorToolbar';
import { EditorPreview } from '../editor/components/EditorPreview';
import { EditPanel } from '../editor/components/EditPanel';
import { EditModal } from '../editor/components/EditModal';
import { ImageEditModal } from '../editor/components/ImageEditModal';
import { useContentModel } from '../editor/hooks/useContentModel';
import { useStagingSession } from '../editor/hooks/useStagingSession';
import type { EditorViewport, EditableElement } from '../editor/types';
import type { ElementClickInfo } from '../editor/components/EditorPreview';

// Customer-scoped API functions
import {
  getEditorContent,
  getEditorPreview,
  startStagingSession,
  saveStagingElement,
  refreshStagingLock,
  releaseStagingLock,
} from '../api/editor';

// =============================================================================
// HELPERS
// =============================================================================

/** Map iframe element kind to content model element type(s) */
const iframeKindToModelType: Record<string, string[]> = {
  heading: ['text', 'rich-text'],
  text: ['text', 'rich-text'],
  image: ['image'],
  button: ['link'],
};

/** Check if an element label indicates a heading */
function isHeadingLabel(label?: string): boolean {
  if (!label) return false;
  const lower = label.toLowerCase();
  return lower.includes('heading') || lower.includes('title') || lower.includes('name') || lower.includes('headline');
}

/** Determine the iframe element type category for postMessage */
function getCategoryForIframe(element: EditableElement): string {
  if (element.type === 'image') return 'image';
  if (element.type === 'link') return 'cta';
  if (isHeadingLabel(element.label)) return 'heading';
  return 'text';
}

// =============================================================================
// COMPONENT
// =============================================================================

export default function CustomerEditorPage() {
  const navigate = useNavigate();
  const { customer } = useAuth();

  // The customer-scoped API doesn't need a real websiteId;
  // we pass a placeholder that the portal API functions ignore
  const websiteId = 'customer-session';
  // The customer-scoped hooks keep the sentinel; anything that calls the
  // STANDARD routes (structural changes, media, image generation) needs the
  // real id — the ownership gate authorises the customer on it.
  const realWebsiteId = customer?.websiteId || '';
  const galleryEditing = Boolean(customer?.features?.galleryEditing) && Boolean(realWebsiteId);

  // Content model with customer-scoped fetcher
  const content = useContentModel(websiteId, {
    fetchContent: getEditorContent,
  });

  // Staging session with customer-scoped API functions
  const staging = useStagingSession(
    websiteId,
    customer ? { id: customer.id || 'customer', name: customer.businessName || customer.email } : null,
    {
      startSession: startStagingSession,
      saveElement: saveStagingElement,
      refreshLock: refreshStagingLock,
      releaseLock: releaseStagingLock,
    }
  );

  // Editor state
  const [activePage, setActivePage] = useState('home');
  const [viewport, setViewport] = useState<EditorViewport>('desktop');
  const [zoomLevel, setZoomLevel] = useState(100);
  // iframeSectionId: the iframe's data-section-id (e.g., "hero_0")
  const [iframeSectionId, setIframeSectionId] = useState<string | null>(null);
  // modelSectionId: the content model's section ID (e.g., "home_hero_0")
  const [modelSectionId, setModelSectionId] = useState<string | null>(null);

  // Edit modal state — stores element + iframe context for postMessage
  const [editModal, setEditModal] = useState<{
    element: EditableElement;
    modelSectionId: string;
    iframeSectionId: string;
    elementKind: string;
    elementIndex: number;
  } | null>(null);

  // Bumped to force a preview reload after a structural change.
  const [refreshKey, setRefreshKey] = useState(0);

  // Canvas add-photo flow (gallery_editing customers only)
  const [addingToGallery, setAddingToGallery] = useState<string | null>(null);

  // Approve state
  const [isApproving, setIsApproving] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const feedbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Set initial page when content loads
  useEffect(() => {
    if (content.pageList.length > 0 && !content.pageList.find((p) => p.slug === activePage)) {
      setActivePage(content.pageList[0].slug);
    }
  }, [content.pageList, activePage]);

  // =============================================================================
  // SECTION & ELEMENT MATCHING (matches dashboard EditorPage pattern)
  // =============================================================================

  /**
   * Match an iframe section ID (like "hero_0") to the content model section.
   * Content model IDs are page-prefixed (like "home_hero_0").
   */
  const findContentSection = useCallback(
    (iframeSecId: string, sectionType: string) => {
      const sections = content.getSections(activePage);
      // Dashboard pattern: check if content model ID ends with the iframe ID
      const matched = sections.find(
        (s) => s.id.endsWith(`_${iframeSecId}`) || s.id === iframeSecId
      );
      // Fallback to type match
      return matched || sections.find((s) => s.type === sectionType) || null;
    },
    [content, activePage]
  );

  /**
   * Derive the iframe section ID from a content model section ID.
   * "home_hero_0" → "hero_0" (strip the page prefix)
   */
  const toIframeSectionId = useCallback((modelSecId: string): string => {
    const parts = modelSecId.split('_');
    // If there are 3+ parts (page_type_index), drop the first part
    return parts.length >= 3 ? parts.slice(1).join('_') : modelSecId;
  }, []);

  /** After a structural change: refetch the model and reload the canvas. */
  const refreshAfterStructural = useCallback(async () => {
    await content.reload();
    setIframeSectionId(null);
    setModelSectionId(null);
    setRefreshKey((k) => k + 1);
  }, [content]);

  const runStructural = useCallback(
    async (changes: Parameters<typeof applyStructuralChanges>[1]) => {
      try {
        const result = await applyStructuralChanges(realWebsiteId, changes);
        if (!result?.success) throw new Error(result?.error || 'That change could not be applied.');
      } catch (err) {
        setFeedbackMessage({ type: 'error', text: (err as Error).message });
      } finally {
        await refreshAfterStructural();
      }
    },
    [realWebsiteId, refreshAfterStructural]
  );

  const handleGalleryAdd = useCallback((modelSecId: string) => {
    if (!galleryEditing) return;
    setAddingToGallery(modelSecId);
  }, [galleryEditing]);

  const handleAddPhotos = useCallback(
    async (items: Array<{ url: string; alt: string; caption?: string; position?: string }>) => {
      if (!addingToGallery || items.length === 0) return;
      setAddingToGallery(null);
      await runStructural(items.map((item) => ({
        id: crypto.randomUUID(),
        action: 'add_gallery_image' as const,
        target: { page: activePage, section: addingToGallery },
        data: { url: item.url, alt: item.alt, caption: item.caption, position: item.position },
      })));
    },
    [addingToGallery, activePage, runStructural]
  );

  const handleGalleryReorder = useCallback(
    (modelSecId: string, from: number, to: number) => {
      if (!galleryEditing) return;
      void runStructural([{
        id: crypto.randomUUID(),
        action: 'move_gallery_image' as const,
        target: { page: activePage, section: modelSecId, index: from },
        data: { toIndex: to },
      }]);
    },
    [galleryEditing, activePage, runStructural]
  );

  const handleGalleryRemove = useCallback(
    (modelSecId: string, index: number) => {
      if (!galleryEditing) return;
      void runStructural([{
        id: crypto.randomUUID(),
        action: 'remove_gallery_image' as const,
        target: { page: activePage, section: modelSecId, index },
        data: {},
      }]);
    },
    [galleryEditing, activePage, runStructural]
  );

  const handleSectionClick = useCallback(
    (sectionId: string, sectionType: string) => {
      // sectionId here is the iframe's data-section-id (e.g., "hero_0")
      setIframeSectionId(sectionId);

      const section = findContentSection(sectionId, sectionType);
      setModelSectionId(section?.id || null);
    },
    [findContentSection]
  );

  const handleElementClick = useCallback(
    (info: ElementClickInfo) => {
      // Update section selection
      setIframeSectionId(info.sectionId);
      const section = findContentSection(info.sectionId, info.sectionType);
      if (!section) return;
      setModelSectionId(section.id);

      // A stamped section sends the element's own id — trust it outright.
      // The category-count matching below is the frozen-section fallback, and
      // for named sections it resolves to the wrong element (the dashboard
      // shipped that bug; the portal gets the fix with the same components).
      if (info.elementId) {
        const exact = section.elements.find((el) => el.id === info.elementId);
        if (exact) {
          setEditModal({
            element: exact,
            modelSectionId: section.id,
            iframeSectionId: info.sectionId,
            elementKind: info.elementKind,
            elementIndex: info.elementIndex,
          });
          return;
        }
      }

      // Map iframe element kind to content model element types
      const allowedTypes = iframeKindToModelType[info.elementKind] || ['text'];

      // Filter candidates by type
      let candidates = section.elements.filter((el) => allowedTypes.includes(el.type));

      // For text vs heading distinction: refine candidates
      if (info.elementKind === 'heading') {
        candidates = candidates.filter((el) => isHeadingLabel(el.label));
      } else if (info.elementKind === 'text') {
        candidates = candidates.filter((el) => !isHeadingLabel(el.label));
      }

      // Match by index, then value, then first
      let element: EditableElement | undefined = candidates[info.elementIndex];

      if (!element && info.currentValue) {
        const stripHtml = (html: string) => html.replace(/<[^>]*>/g, '').trim();
        const clickedText = stripHtml(info.currentValue);
        element = candidates.find((el) => {
          const elText = stripHtml(Array.isArray(el.value) ? el.value.join(' ') : el.value);
          return elText === clickedText || elText.includes(clickedText) || clickedText.includes(elText);
        });
      }

      if (!element) element = candidates[0];

      if (element) {
        setEditModal({
          element,
          modelSectionId: section.id,
          iframeSectionId: info.sectionId,
          elementKind: info.elementKind,
          elementIndex: info.elementIndex,
        });
      }
    },
    [findContentSection]
  );

  // =============================================================================
  // PREVIEW UPDATE (postMessage to iframe)
  // =============================================================================

  /**
   * Send a live DOM update to the iframe preview.
   * Uses the iframe section ID directly (no re-derivation needed).
   */
  const sendIframeUpdate = useCallback(
    (iframeSecId: string, elementKind: string, elementIndex: number, newValue: string) => {
      const iframe = document.querySelector('iframe');
      if (!iframe?.contentWindow) return;

      if (elementKind === 'image') {
        iframe.contentWindow.postMessage(
          {
            type: 'niftee-update-image',
            sectionId: iframeSecId,
            elementIndex,
            newValue,
          },
          '*'
        );
      } else {
        iframe.contentWindow.postMessage(
          {
            type: 'niftee-update-text',
            sectionId: iframeSecId,
            elementType: elementKind,
            elementIndex,
            newValue,
          },
          '*'
        );
      }
    },
    []
  );

  /**
   * Resolve an element ID to its iframe section ID, kind, and index.
   * Used by sidebar panel changes where we don't have iframe context.
   */
  const resolveElementContext = useCallback(
    (elementId: string): { iframeSecId: string; kind: string; index: number } | null => {
      const sections = content.getSections(activePage);
      for (const section of sections) {
        const element = section.elements.find((el) => el.id === elementId);
        if (!element) continue;

        const kind = getCategoryForIframe(element);

        // Find index among same-kind siblings
        let sameKindElements: EditableElement[];
        if (kind === 'image') {
          sameKindElements = section.elements.filter((el) => el.type === 'image');
        } else if (kind === 'cta') {
          sameKindElements = section.elements.filter((el) => el.type === 'link');
        } else if (kind === 'heading') {
          sameKindElements = section.elements.filter((el) => el.type === 'text' && isHeadingLabel(el.label));
        } else {
          sameKindElements = section.elements.filter((el) => el.type === 'text' && !isHeadingLabel(el.label));
        }

        const index = Math.max(0, sameKindElements.findIndex((el) => el.id === elementId));
        const iframeSecId = toIframeSectionId(section.id);

        return { iframeSecId, kind, index };
      }
      return null;
    },
    [content, activePage, toIframeSectionId]
  );

  // =============================================================================
  // SAVE HANDLERS
  // =============================================================================

  /**
   * Persist first, then show the change. A refused save (another editor holds
   * the lock, the text is too short, the page moved on) used to leave the new
   * text on screen with nothing saved, and it vanished on the next visit.
   */
  const persistElement = useCallback(
    async (elementId: string, oldValue: string, newValue: string): Promise<boolean> => {
      const ok = await staging.saveElement(elementId, oldValue, newValue);
      if (!ok) {
        setFeedbackMessage({
          type: 'error',
          text:
            staging.lastSaveError() ||
            "That change wasn't saved. Please reload the page and try again.",
        });
        return false;
      }
      setFeedbackMessage((prev) => (prev?.type === 'error' ? null : prev));
      return true;
    },
    [staging]
  );

  /** Save from EditModal / ImageEditModal (we have iframe context) */
  const handleModalSave = useCallback(
    async (elementId: string, newValue: string, oldValue: string) => {
      if (!(await persistElement(elementId, oldValue, newValue))) return;

      // Keep the sidebar and preview in step with what is now saved
      content.updateElement(elementId, newValue);
      if (editModal) {
        sendIframeUpdate(
          editModal.iframeSectionId,
          editModal.elementKind,
          editModal.elementIndex,
          newValue
        );
      }
    },
    [content, persistElement, editModal, sendIframeUpdate]
  );

  /** Save from EditPanel sidebar (need to resolve iframe context from element ID) */
  const handlePanelElementChange = useCallback(
    async (elementId: string, oldValue: string, newValue: string) => {
      // Resolve iframe context BEFORE updating state (needs current content model)
      const ctx = resolveElementContext(elementId);

      if (!(await persistElement(elementId, oldValue, newValue))) return;

      content.updateElement(elementId, newValue);
      if (ctx) {
        sendIframeUpdate(ctx.iframeSecId, ctx.kind, ctx.index, newValue);
      }
    },
    [content, persistElement, resolveElementContext, sendIframeUpdate]
  );

  // =============================================================================
  // OTHER HANDLERS
  // =============================================================================

  const handleApprove = useCallback(async () => {
    setIsApproving(true);
    setFeedbackMessage(null);
    try {
      await approveSite();
      setFeedbackMessage({ type: 'success', text: 'Site approved! It will be going live soon.' });
      if (feedbackTimeoutRef.current) clearTimeout(feedbackTimeoutRef.current);
      feedbackTimeoutRef.current = setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err) {
      setFeedbackMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to approve site',
      });
    } finally {
      setIsApproving(false);
    }
  }, [navigate]);

  const handleRequestChanges = useCallback(() => {
    navigate('/revisions');
  }, [navigate]);

  const handleBack = useCallback(() => {
    navigate('/');
  }, [navigate]);

  // =============================================================================
  // RENDER
  // =============================================================================

  if (content.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          <span className="text-sm text-gray-500">Loading editor...</span>
        </div>
      </div>
    );
  }

  if (content.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white rounded-lg shadow-sm p-6 max-w-md text-center">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Unable to Load Editor</h2>
          <p className="text-gray-600 mb-4">{content.error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!content.hasContent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white rounded-lg shadow-sm p-6 max-w-md text-center">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">No Content Available</h2>
          <p className="text-gray-600 mb-4">Your website hasn't been generated yet.</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Get current page sections for the side panel
  const currentSections = content.getSections(activePage);
  const selectedSection = modelSectionId
    ? currentSections.find((s) => s.id === modelSectionId)
    : null;

  // Filter out adminOnly elements for customer mode and map types to EditPanel's expected types
  const filteredElements = (selectedSection?.elements.filter((el) => !el.adminOnly) || []).map((el) => {
    // EditPanel expects 'text' | 'image' | 'cta'; map 'link' → 'cta', others → 'text'
    let panelType: 'text' | 'image' | 'cta' = 'text';
    if (el.type === 'image') panelType = 'image';
    else if (el.type === 'link') panelType = 'cta';
    return {
      id: el.id,
      type: panelType,
      label: el.label,
      value: Array.isArray(el.value) ? el.value.join('\n') : el.value,
      placeholder: el.placeholder,
      constraints: el.constraints,
    };
  });

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      {/* Toolbar */}
      <EditorToolbar
        businessName={content.website?.businessName || 'Your Website'}
        activePage={activePage}
        pages={content.pageList}
        viewport={viewport}
        zoomLevel={zoomLevel}
        dirtyCount={staging.dirtyCount}
        deploying={isApproving}
        mode="customer"
        onPageChange={setActivePage}
        onViewportChange={setViewport}
        onZoomChange={setZoomLevel}
        onBack={handleBack}
        onDeploy={() => {}} // Not used in customer mode
        onApprove={handleApprove}
        onRequestChanges={handleRequestChanges}
      />

      {/* Feedback toast */}
      {feedbackMessage && (
        <div
          className={`px-4 py-2 text-sm font-medium text-center ${
            feedbackMessage.type === 'success'
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}
        >
          {feedbackMessage.text}
        </div>
      )}

      {/* Main content area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Preview */}
        <div className="flex-1 overflow-hidden">
          <EditorPreview
            websiteId={websiteId}
            pageSlug={activePage}
            viewport={viewport}
            zoomLevel={zoomLevel}
            selectedSectionId={iframeSectionId}
            refreshKey={refreshKey}
            onNavigate={setActivePage}
            onSectionClick={handleSectionClick}
            onElementClick={handleElementClick}
            onGalleryAdd={handleGalleryAdd}
            onGalleryReorder={handleGalleryReorder}
            onGalleryRemove={handleGalleryRemove}
            canvasGalleryEditing={galleryEditing}
            fetchPreview={getEditorPreview}
          />
        </div>

        {/* Side panel */}
        {selectedSection && filteredElements.length > 0 && (
          <EditPanel
            sectionId={selectedSection.id}
            sectionLabel={selectedSection.label}
            sectionType={selectedSection.type}
            elements={filteredElements}
            websiteId={realWebsiteId}
            activePage={activePage}
            mode="customer"
            allowGalleryPhotos={galleryEditing}
            onChange={(elementId, newValue) => {
              const origEl = selectedSection?.elements.find((el) => el.id === elementId);
              const oldValue = origEl ? (Array.isArray(origEl.value) ? origEl.value.join('\n') : origEl.value) : '';
              handlePanelElementChange(elementId, oldValue, newValue);
            }}
            onClose={() => {
              setIframeSectionId(null);
              setModelSectionId(null);
            }}
          />
        )}
      </div>

      {/* Canvas add-photo flow */}
      {addingToGallery && galleryEditing && (
        <ImageEditModal
          currentValue=""
          label="Add photos"
          mode="add"
          websiteId={realWebsiteId}
          onAddMany={(items) => void handleAddPhotos(items)}
          onSave={(url, alt, caption, position) =>
            void handleAddPhotos([{ url, alt, caption: caption || undefined, position: position || undefined }])
          }
          onClose={() => setAddingToGallery(null)}
        />
      )}

      {/* Edit modal — use ImageEditModal for images, EditModal for text/buttons */}
      {editModal && (() => {
        const el = editModal.element;
        const currentValue = typeof el.value === 'string' ? el.value : el.value.join('\n');

        if (el.type === 'image') {
          return (
            <ImageEditModal
              currentValue={currentValue}
              label={el.label}
              websiteId={realWebsiteId}
              onSave={(newUrl, _altText, _title) => {
                handleModalSave(el.id, newUrl, currentValue);
                setEditModal(null);
              }}
              onClose={() => setEditModal(null)}
            />
          );
        }

        // Map editor type to EditModal's elementKind
        let elementKind: 'heading' | 'text' | 'image' | 'button' = 'text';
        if (el.type === 'link') elementKind = 'button';
        else if (isHeadingLabel(el.label)) elementKind = 'heading';

        return (
          <EditModal
            elementKind={elementKind}
            currentValue={currentValue}
            label={el.label}
            mode="customer"
            onSave={(newValue) => handleModalSave(el.id, newValue, currentValue)}
            onClose={() => setEditModal(null)}
          />
        );
      })()}
    </div>
  );
}
